import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useCurrentUser } from '../auth';
import { listAccounts } from './api';
import { buildAccountTree, type AccountTreeNode } from './tree';
import { searchAccounts } from './search';

function accountTypeBadge(type: string): string {
  switch (type) {
    case 'brand':
      return 'bg-indigo-100 text-indigo-700';
    case 'group':
      return 'bg-amber-100 text-amber-700';
    case 'location':
      return 'bg-emerald-100 text-emerald-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

// Simple debounce hook. 200ms is long enough to coalesce a rapid typist's
// keystrokes into a single pass, short enough that results still feel
// responsive. No effect dependency on the ms value since we only read it
// once per mount.
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

// Highlights every case-insensitive match of `query` inside `text`.
// Returns a ReactNode with <mark> spans around hits. Empty query => plain text.
function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const lowerText = text.toLowerCase();
  const lowerQ = query.toLowerCase();
  const parts: Array<{ value: string; match: boolean }> = [];
  let cursor = 0;
  while (cursor < text.length) {
    const hit = lowerText.indexOf(lowerQ, cursor);
    if (hit < 0) {
      parts.push({ value: text.slice(cursor), match: false });
      break;
    }
    if (hit > cursor) parts.push({ value: text.slice(cursor, hit), match: false });
    parts.push({ value: text.slice(hit, hit + query.length), match: true });
    cursor = hit + query.length;
  }
  return (
    <>
      {parts.map((p, i) =>
        p.match ? (
          <mark key={i} className="bg-yellow-200 rounded-sm px-0.5">
            {p.value}
          </mark>
        ) : (
          <span key={i}>{p.value}</span>
        ),
      )}
    </>
  );
}

type TreeRowProps = {
  node: AccountTreeNode;
  expanded: Set<string>;
  matchIds: Set<string>;
  visibleIds: Set<string> | null; // null = show all (no active query)
  query: string;
  onToggle: (id: string) => void;
};

function TreeRow({ node, expanded, matchIds, visibleIds, query, onToggle }: TreeRowProps) {
  // Filter children to those allowed by the current visibility set.
  const visibleChildren = visibleIds
    ? node.children.filter((c) => visibleIds.has(c.account.id))
    : node.children;
  const hasChildren = visibleChildren.length > 0;
  const isOpen = expanded.has(node.account.id);
  const isMatch = matchIds.has(node.account.id);

  return (
    <>
      <tr className={`hover:bg-slate-50 border-t border-slate-100 ${isMatch ? 'bg-yellow-50/50' : ''}`}>
        <td className="px-4 py-2" style={{ paddingLeft: 16 + node.depth * 24 }}>
          <div className="flex items-center gap-2">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => onToggle(node.account.id)}
                aria-label={isOpen ? 'Collapse' : 'Expand'}
                className="w-5 h-5 inline-flex items-center justify-center rounded hover:bg-slate-200 text-slate-500"
              >
                {isOpen ? '▾' : '▸'}
              </button>
            ) : (
              <span className="w-5" />
            )}
            <Link to={`/accounts/${node.account.id}`} className="font-medium text-slate-900 hover:underline">
              <Highlight text={node.account.name} query={query} />
            </Link>
            <span className={`text-xs px-2 py-0.5 rounded-full ${accountTypeBadge(node.account.accountType)}`}>
              {node.account.accountType}
            </span>
            {node.account.status !== 'active' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">
                {node.account.status}
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-2 font-mono text-xs text-slate-600">{node.account.slug}</td>
        <td className="px-4 py-2 text-sm text-slate-600">{node.resolvedCurrency}</td>
        <td className="px-4 py-2 text-sm text-slate-600">{node.account.industry}</td>
      </tr>
      {isOpen &&
        visibleChildren.map((child) => (
          <TreeRow
            key={child.account.id}
            node={child}
            expanded={expanded}
            matchIds={matchIds}
            visibleIds={visibleIds}
            query={query}
            onToggle={onToggle}
          />
        ))}
    </>
  );
}

// `embedded` hides the page title + New-account button (the host AccountsPage
// renders them alongside the Tree/Grid toggle). The tree-specific controls
// (Expand/Collapse, search) always render.
export function AccountList({ embedded = false }: { embedded?: boolean } = {}) {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const [expandedByUser, setExpandedByUser] = useState<Set<string>>(new Set());
  const [searchInput, setSearchInput] = useState('');
  const query = useDebounced(searchInput.trim(), 200);
  const searchRef = useRef<HTMLInputElement>(null);

  const tree = useMemo(
    () => (accounts.data ? buildAccountTree(accounts.data) : []),
    [accounts.data],
  );

  // Pure helper: empty query -> visibleIds null (show all); else matches +
  // all their ancestors. See src/modules/accounts/search.ts for rationale
  // (summary: matching on name catches suburb-style searches because
  // location names include the suburb by import-time convention).
  const { visibleIds, matchIds } = useMemo(
    () => searchAccounts(accounts.data ?? [], query),
    [accounts.data, query],
  );

  // When a query is active, auto-expand everything in the visible set so the
  // ancestors of matched rows are shown without the operator having to click.
  // When the query is empty, fall back to the user's manual expand/collapse.
  const effectiveExpanded = useMemo(
    () => (visibleIds ? visibleIds : expandedByUser),
    [visibleIds, expandedByUser],
  );

  if (accounts.isLoading) return <CenteredSpinner />;
  if (accounts.error) {
    return (
      <div className="text-red-600 text-sm">
        Failed to load accounts: {(accounts.error as Error).message}
      </div>
    );
  }

  const toggle = (id: string) => {
    if (visibleIds) return; // expand/collapse disabled while searching
    setExpandedByUser((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpandedByUser(new Set((accounts.data ?? []).map((a) => a.id)));
  const collapseAll = () => setExpandedByUser(new Set());

  const visibleRoots = visibleIds ? tree.filter((n) => visibleIds.has(n.account.id)) : tree;
  const matchCount = matchIds.size;

  // Enter on the search input jumps to the single match when there's exactly
  // one. Useful for "type the suburb, hit enter" muscle memory.
  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key !== 'Enter') return;
    if (matchCount !== 1) return;
    const onlyId = [...matchIds][0]!;
    navigate(`/accounts/${onlyId}`);
  };

  return (
    <div>
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Accounts</h1>
            <p className="text-sm text-slate-600">Every client relationship, at every hierarchy level.</p>
          </div>
          {user.role === 'admin' && (
            <Link to="/accounts/new">
              <Button>New account</Button>
            </Link>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <Button variant="outline" onClick={expandAll} disabled={visibleIds !== null}>
          Expand all
        </Button>
        <Button variant="outline" onClick={collapseAll} disabled={visibleIds !== null}>
          Collapse all
        </Button>
      </div>

      <div className="mb-4">
        <Input
          ref={searchRef}
          type="search"
          placeholder="Search accounts by name…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={onKeyDown}
          className="max-w-md"
        />
        {query !== '' && (
          <p className="mt-1 text-xs text-slate-600">
            {matchCount === 0
              ? 'No matches.'
              : matchCount === 1
                ? '1 match. Press Enter to open.'
                : `${matchCount} matches. Ancestors shown for context.`}
          </p>
        )}
      </div>

      {tree.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          No accounts yet.
          {user.role === 'admin' && ' Click "New account" to create a brand-level root account.'}
        </div>
      ) : visibleRoots.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          No accounts match "{query}".
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Slug</th>
                <th className="px-4 py-3 font-medium">Currency</th>
                <th className="px-4 py-3 font-medium">Industry</th>
              </tr>
            </thead>
            <tbody>
              {visibleRoots.map((node) => (
                <TreeRow
                  key={node.account.id}
                  node={node}
                  expanded={effectiveExpanded}
                  matchIds={matchIds}
                  visibleIds={visibleIds}
                  query={query}
                  onToggle={toggle}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

